import { FC, useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";

import { useAppDispatch } from "../../store/hooks";
import { fetchTags } from "../../store/tagStore/tagThunk";
import { ITag, TagAppliesTo } from "../../utils/tag.types";

interface TagSelectProps {
  /** Currently selected tag ids. */
  value?: string[];
  onChange: (tagIds: string[]) => void;
  disabled?: boolean;
}

/**
 * Invoice tag picker. Offers active tags from the account's Tag master whose
 * `appliesTo` includes INVOICE (the API also returns generic tags). Options are
 * kept in local state so this does not disturb the tags slice used by the
 * Tags master screen in Settings.
 */
const TagSelect: FC<TagSelectProps> = ({ value, onChange, disabled }) => {
  const dispatch = useAppDispatch();

  const [options, setOptions] = useState<ITag[]>([]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const data: any = await dispatch(
          fetchTags(`appliesTo=${TagAppliesTo.INVOICE}&status=active`)
        ).unwrap();

        setOptions(data || []);
      } catch (error) {
        console.error('Error fetching invoice tags:', error);
      }
    };

    loadTags();
  }, [dispatch]);

  // `value` holds ids; map the first id back onto the loaded options for single-select display.
  const selectedId = Array.isArray(value) && value.length > 0 ? value[0] : null;
  const selected = options.find((option) => option._id === selectedId) || null;

  return (
    <>
      <h5 className='c-label'>Tags</h5>
      <Autocomplete
        size='small'
        disabled={disabled}
        options={options}
        value={selected}
        onChange={(event, newValue: ITag | null) => onChange(newValue ? [newValue._id] : [])}
        getOptionLabel={(option: ITag) => option.name}
        isOptionEqualToValue={(option: ITag, val: ITag) => option._id === val._id}
        renderOption={(props, option: ITag) => (
          <li {...props} key={option._id} style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              backgroundColor: option.color || '#7f8c9b',
              display: 'inline-block', flexShrink: 0
            }} />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            className='c-text-field'
            variant="standard"
            placeholder={selected ? '' : 'Select Tag'}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              startAdornment: (
                <>
                  {selected && (
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: selected.color || '#7f8c9b',
                        display: 'inline-block',
                        marginRight: 6,
                        marginLeft: 4,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ width: '100%', marginBottom: '3px' }}
      />
    </>
  );
};

export default TagSelect;
